import type React from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { authApi } from './apis';

export interface User {
  id?: number;
  username?: string;
  email?: string;
  roles?: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null; // access token
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize from storage synchronously to avoid a brief unauthenticated state on first render
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('accessToken') || localStorage.getItem('token'));
  const [user, setUser] = useState<User | null>(() => {
    const u = localStorage.getItem('user');
    if (!u) return null;
    try { return JSON.parse(u); } catch { return null; }
  });

  const login = async (username: string, password: string) => {
    const res: any = await authApi.post<any>('/admin/api/auth/login', { username, password });
    const accessToken = res?.accessToken ?? res?.data?.accessToken ?? res?.token ?? res?.data?.token;
    const refreshToken = res?.refreshToken ?? res?.data?.refreshToken ?? null;
    const newUser = res?.user ?? res?.data?.user ?? { username };
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('token', accessToken); // backward compatibility
    }
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    setToken(accessToken ?? null);
    setUser(newUser);
  };

  const logout = () => {
    try { (authApi.post as any)('/admin/api/auth/logout', {}); } catch {}
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, token, login, logout, isAuthenticated: !!token }),
    [user, token]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
